// Every subject a browser client speaks, built, never inlined (how-we-build §
// the subject is the contract). Every subject starts with the root, `impire`
// (decision 0030): a client writes the tokenless family form —
// `impire.workloads.register` — and the import boundary fills the account
// slot on the wire. The address starts after the root: a reader of a wire
// subject takes its tokens from `address`, never from a position counted from
// the start. The platform form with the slot, the subscription filter a
// service makes and the import a tenant carries are a service's, and a client
// that can even express an account subject is the footgun the posture names
// (how-we-build § thin clients): none of them exist here.
/**
 * ROOT is the first token of every subject the fleet speaks: one literal
 * naming the platform, under which every family lives (decision 0030). The
 * broker's own subjects — discovery, the system account, inboxes — are not
 * under it. It is a constant: the day two installs share one system is the
 * day it becomes the install's.
 */
export const ROOT = "impire";
/** Family is the first token of a subject's address: the service that serves it. */
export const Family = {
    /** Account-local: served inside the account it belongs to, never exported. */
    vault: "vault",
    /** Served in the platform account and imported into every tenant. */
    identity: "identity",
    /** The workload service's, served in the platform account and imported into every tenant. */
    workloads: "workloads",
    /** Tenant-local: one family per workload id, served by its placement. */
    workload: "workload",
    /** The sign-in records the auth service writes; the tenant is the second token. */
    audit: "audit",
    /** The placement records the executor publishes; the tenant is the second token. */
    placements: "placements",
    /** The fleet record and the executor verbs, platform-internal. */
    fleet: "fleet",
    /** The configuration service's, served in the platform account and imported into every tenant (02-DESIGN/06-configuration/01-the-configuration-service.md). */
    config: "config",
    /** The guardrails service's, served in the platform account and imported into every tenant (02-DESIGN/07-guardrails/03-the-guardrails-service.md). */
    guard: "guard",
    /** Account-local: the state service placed in the tenant serves it (02-DESIGN/10-state/02-the-state-service.md). */
    state: "state",
    /** The solutions service's, served in the platform account and imported into every tenant (02-DESIGN/14-solutions/01-the-manifest.md). */
    solutions: "solutions",
    /** The blueprint service's, served in the platform account and imported into every tenant (02-DESIGN/13-blueprints/01-the-blueprint.md). */
    blueprint: "blueprint",
    /** The billing service's, served in the platform account and imported into every tenant (02-DESIGN/18-billing/01-the-billing-service.md § the verbs). */
    billing: "billing",
};
/**
 * The two tokens reserved after a workload's id in every workload's family
 * (02-DESIGN/04-workloads/01-the-workload-model.md § what a workload serves):
 * `infer`, under which an inference instance serves the inference contract,
 * and `tool`, under which a toolkit serves its tools. `cancel` is the control
 * token in the inference contract's capability position.
 */
export const TOKEN_INFER = "infer";
export const TOKEN_TOOL = "tool";
export const TOKEN_CANCEL = "cancel";
/** SEP separates tokens. */
export const SEP = ".";
/**
 * validToken reports whether s is exactly one subject token: non-empty,
 * printable ASCII without space, dot or wildcard.
 */
export function validToken(s) {
    if (s === "") {
        return false;
    }
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c <= 0x20 || c > 0x7e || c === 0x2e /* . */ || c === 0x2a /* * */ || c === 0x3e /* > */) {
            return false;
        }
    }
    return true;
}
/**
 * RESERVED_SLUGS are the words a slug may not be, because they name a path at
 * the install's names: `auth`, the auth service's surfaces beside the console
 * (02-DESIGN/03-console/01-the-console.md § the address), and the gateway's
 * four face names, under which a workspace's slug would shadow a face
 * (02-DESIGN/09-gateway/02-the-oauth-face.md § the address). `.well-known`
 * is no slug by its shape. The Go library's `subject.ReservedSlugs`.
 */
export const RESERVED_SLUGS = ["auth", "mcp", "openai", "anthropic", "jev"];
/**
 * validSlug reports whether s is a tenant slug: lowercase letters, digits and
 * hyphens, beginning with a letter, and not one of RESERVED_SLUGS.
 */
export function validSlug(s) {
    if (s === "" || s.length > 63 || RESERVED_SLUGS.includes(s)) {
        return false;
    }
    return /^[a-z][a-z0-9-]*$/.test(s);
}
/**
 * tokens splits a string on the separator: a path, a selector, a subject with
 * its root. A reader of a wire subject uses `address`.
 */
export function tokens(subj) {
    return subj.split(SEP);
}
/**
 * address is the tokens of a wire subject after the root: the family first,
 * then the rest. It is the only way to read a subject by position, and it
 * throws for a subject that is not under the root.
 */
export function address(subj) {
    const toks = tokens(subj);
    if (toks.length < 2 || toks[0] !== ROOT) {
        throw new Error(`subject: "${subj}" is not under the root`);
    }
    return toks.slice(1);
}
/** join joins tokens into a subject. It does not validate them. */
export function join(...parts) {
    return parts.join(SEP);
}
/** client builds the tokenless form a client writes: `impire.<family>.<tokens…>`. */
export function client(f, ...parts) {
    return join(ROOT, f, ...parts);
}
/**
 * workloadInfer is `impire.workload.<id>.infer.<capability>`: one capability of the
 * inference instance that is the workload (02-DESIGN/05-inference/02-the-inference-contract.md § subjects).
 */
export function workloadInfer(id, capability) {
    return client(Family.workload, id, TOKEN_INFER, capability);
}
/** workloadInferCancel is `impire.workload.<id>.infer.cancel.<request>`: the subject a caller publishes to abort the request the first frame named. */
export function workloadInferCancel(id, request) {
    return client(Family.workload, id, TOKEN_INFER, TOKEN_CANCEL, request);
}
/** workloadTool is `impire.workload.<id>.tool.<tool>`: one tool of the toolkit that is the workload (02-DESIGN/08-toolkits/01-the-toolkit-contract.md § subjects). */
export function workloadTool(id, tool) {
    return client(Family.workload, id, TOKEN_TOOL, tool);
}
/** DISCOVERY is the service-discovery prefix every template admits. */
export const DISCOVERY = "$SRV";
/** discoveryFilter is the subscription a template admits for discovery: `$SRV.>`. */
export function discoveryFilter() {
    return join(DISCOVERY, ">");
}
/** serviceInfo is the subject a caller asks for one service's info: `$SRV.INFO.<name>`. */
export function serviceInfo(name) {
    return join(DISCOVERY, "INFO", name);
}
/** USER_INFO is the server's own user-info request, which every template admits. */
export const USER_INFO = "$SYS.REQ.USER.INFO";
/** INBOX_PREFIX is the root of every reply subject. */
export const INBOX_PREFIX = "_INBOX";
/** inbox is the inbox prefix a client uses for the inbox token it minted: `_INBOX.<token>`. */
export function inbox(token) {
    return join(INBOX_PREFIX, token);
}
/** inboxFilter is the only inbox subscription a template admits for a placement: `_INBOX.<token>.>`. */
export function inboxFilter(token) {
    return join(INBOX_PREFIX, token, ">");
}
/** INBOX_TAG is the tag the auth service stamps on every placement. */
export const INBOX_TAG = "inbox";
/** tag renders a template function for a tag, `{{tag(<key>)}}`. */
export function tag(key) {
    return `{{tag(${key})}}`;
}
/**
 * inboxTemplate is the inbox entry of every role template, expanded by the
 * broker from the inbox tag (decision 0006): `_INBOX.{{tag(inbox)}}.>`.
 */
export function inboxTemplate() {
    return join(INBOX_PREFIX, tag(INBOX_TAG), ">");
}
//# sourceMappingURL=subject.js.map